import { Link, useHistory, useParams } from 'react-router-dom'
import ContentHeader from '../Layout/ContentHeader'
import { useLoading } from '../../LoadingOverlay/LoadingOverlayContext'
import { FormProvider, useForm } from 'react-hook-form'
import InputController from '../Global/InputController'
import Box from '../Global/Box'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  CREATE_GROUP,
  GET_ALL_GROUPS_FOR_SELECT,
  GET_ALL_REPORT_GROUPS_BY,
  GET_ONE_GROUP,
  UPDATE_GROUP,
} from '../../graphql/Catalog/ReportGroups/reportGroups'
import { toastSweetAlert } from '../Helpers/ToastSweetAlert'
import CheckboxCustom from '../Global/Checkbox/CheckboxCustom'
import {
  CREATE_GROUPS_CATEGORIES,
  GET_ONE_GROUPS_CATEGORIES,
  UPDATE_GROUPS_CATEGORIES,
} from '../../graphql/Catalog/ReportGroups/reportCategories'
import { set } from 'lodash'

// const backPage = '/ReportGroups/Categories'

const refetchTable = {
  query: GET_ALL_REPORT_GROUPS_BY,
  variables: {
    offset: 0,
    limit: 10,
    searchQuery: null,
  },
}

const singular = 'Categoría'
const plural = 'Categorías'

const extensions_allowed_image = ['.jpg', '.jpeg', '.png', '.svg']
const sizeMaxFile = 5 //MB

const ReportCategoriesNew = () => {
  //HOOKS:
  const { id: _id } = useParams()
  const history = useHistory()
  const { showLoading, hideLoading } = useLoading()
  const [backPage, setBackPage] = useState(false)

  //STATES:
  const [file, setFile] = useState(null)
  const [icon, setIcon] = useState(null)
  const [allowCreation, setAllowCreation] = useState({
    allow_create_in_app: false,
    allow_create_in_web: false,
    show_system: false,
  })
  const [selectGroups, setSelectGroups] = useState([])
  const [order, setOrder] = useState(0)

  //MUTATIONS:
  const [createItem] = useMutation(CREATE_GROUPS_CATEGORIES, {
    refetchQueries: [refetchTable],
  })

  const [updateItem] = useMutation(UPDATE_GROUPS_CATEGORIES, {
    refetchQueries: [refetchTable],
  })

  //QUERYS:
  const { data, loading, error } = useQuery(GET_ONE_GROUPS_CATEGORIES, {
    skip: !_id,
    variables: {
      getOneGroupsCategoriesId: _id ? parseInt(_id) : -1,
    },
    fetchPolicy: 'no-cache',
  })

  const groups = useQuery(GET_ALL_GROUPS_FOR_SELECT)

  //USE-EFFECTS:
  useEffect(() => {
    if (_id) {
      if (!loading) {
        if (error)
          return toastSweetAlert({
            mode: 'error',
            message: error.message,
          })

        reset({
          name: data.getOneGroupsCategories.name,
          description: data.getOneGroupsCategories.description,
          icon: data.getOneGroupsCategories.icon,
          order: data.getOneGroupsCategories.order
            ? parseInt(data.getOneGroupsCategories.order)
            : 1,
          group_id: data.getOneGroupsCategories.group_id,
          allow_create_in_app: data.getOneGroupsCategories.allow_create_in_app,
          allow_create_in_web: data.getOneGroupsCategories.allow_create_in_web,
          show_system: data.getOneGroupsCategories.show_system,
        })

        let icon = data.getOneGroupsCategories.icon ?? null

        if (
          data.getOneGroupsCategories?.GroupCategoryIconFile?.url &&
          data.getOneGroupsCategories?.GroupCategoryIconFile?.url !== ''
        ) {
          icon = data.getOneGroupsCategories?.GroupCategoryIconFile?.url
        }
        setAllowCreation({
          allow_create_in_app: data.getOneGroupsCategories.allow_create_in_app,
          allow_create_in_web: data.getOneGroupsCategories.allow_create_in_web,
          show_system: data.getOneGroupsCategories.show_system,
        })
        setIcon(icon)
      }
    } else {
      reset({
        name: '',
        description: '',
        icon: '',
        order: 0,
        group_id: '',
        allow_create_in_app: false,
        allow_create_in_web: false,
        show_system: false,
      })
    }
  }, [_id, loading, error, data])

  useEffect(() => {
    if (groups.data) {
      let list = []
      groups.data.getAllGroups.map((item) => {
        return list.push({
          value: item.id,
          label: item.name,
        })
      })
      setSelectGroups(list)
    }
  }, [groups])

  //FORM:
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Campo requerido'),
    description: Yup.string().required('Campo requerido'),
    order: Yup.number().required('Campo requerido'),
    group_id: Yup.number().required('Campo requerido'),
  })

  const {
    methods,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  //FUNCTIONS:
  const handleFile = async (e) => {
    if (!e.target.files[0]) return

    const fileName = e.target.files[0].name.toLowerCase()
    const lastDot = fileName.lastIndexOf('.')
    const extension = fileName.slice(lastDot, fileName.length)

    if (extensions_allowed_image.indexOf(extension) === -1) {
      setFile(null)
      return toastSweetAlert({
        mode: 'error',
        message: `Solo se admite ${extensions_allowed_image.join(', ')}`,
      })
    }

    if (e.target.files[0].size / 1048576 > sizeMaxFile) {
      setFile(null)
      return toastSweetAlert({
        mode: 'error',
        message: `El archivo no puede superar los ${sizeMaxFile}MB`,
      })
    }

    setFile(e.target.files[0])
  }

  const onSubmit = async (data) => {
    showLoading()
    try {
      if (_id) {
        await updateItem({
          variables: {
            input: {
              name: data.name,
              description: data.description,
              file: file,
              icon: data.icon,
              order: data.order ? parseInt(data.order) : 0,
              group_id: parseInt(data.group_id),
              allow_create_in_app: allowCreation.allow_create_in_app,
              allow_create_in_web: allowCreation.allow_create_in_web,
              show_system: allowCreation.show_system,
            },
            updateGroupsCategoriesId: parseInt(_id),
          },
        })
      } else {
        await createItem({
          variables: {
            input: {
              name: data.name,
              description: data.description,
              icon: data.icon,
              file: file,
              order: data.order ? parseInt(data.order) : 0,
              group_id: parseInt(data.group_id),
              allow_create_in_app: allowCreation.allow_create_in_app,
              allow_create_in_web: allowCreation.allow_create_in_web,
              show_system: allowCreation.show_system,
            },
          },
        })
      }

      //REDIRECT AFTER CREATE OR UPDATE WITOUT ROUTE
      history.push(backPage)

      toastSweetAlert({
        mode: 'ok',
        message: singular + ' creado correctamente',
      })
    } catch (error) {
      console.error(error)
      toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    } finally {
      hideLoading()
    }
  }

  return (
    <>
      <ContentHeader
        title=""
        breadcrumb="custom"
        // titleContent={
        //   <div className="col-12 col-sm-6">
        //     <Link
        //       className="btn btn-migrates-white"
        //       //   to={history.push(history.location.pathname.replace(/\/edit/, ''))}
        //       to={backPageS}
        //     >
        //       <i className="fas fa-long-arrow-alt-left" /> Atrás
        //     </Link>
        //   </div>
        // }
        breadcrumbContent={
          <div className="col-12 col-sm-12">
            <ol className="breadcrumb float-sm-right">
              <li className="breadcrumb-item">
                <Link to={backPage}>Grupos de Reportes / </Link>
              </li>
              <li className="breadcrumb-iteem active">
                {` `}
                {_id ? 'Editar' : 'Agregar'}
              </li>
            </ol>
          </div>
        }
        windowTitle={`${_id ? 'Editar' : 'Agregar'} ${singular}`}
      />

      <FormProvider {...methods}>
        <form className="px-3" onSubmit={handleSubmit(onSubmit)}>
          <Box
            title={`${_id ? 'Editar' : 'Agregar'} ${singular}`}
            // btnRedPath={backPageState}
            // btnRedTxt="Cancelar"
            errors={errors}
            btnSubmit={true}
            BackPageHeader
            setBackPage={setBackPage}
            content={
              <div className="mb-3">
                <div className="row">
                  <div className="mb-3 col-12 col-sm-4">
                    <InputController
                      label="Nombre *"
                      name="name"
                      control={control}
                      inputType="text"
                      placeholderTxt="Nombre del Grupo"
                      required
                    />
                    <InputController
                      label="Icono"
                      name="icon"
                      control={control}
                      inputType="hidden"
                      placeholderTxt="Icono del Grupo"
                      valueManual=""
                    />
                  </div>
                  <div className="mb-3 col-12 col-sm-4">
                    <InputController
                      label="Grupo *"
                      name="group_id"
                      control={control}
                      inputType="choosen"
                      placeholderTxt="Grupo al que pertenece"
                      options={selectGroups}
                      addClass="z-index-99"
                      required
                    />
                  </div>
                  <div className="mb-3 col-12 col-sm-4 d-flex flex-row">
                    {icon && (
                      <img src={icon} alt="icon" width="30px" height="30px" />
                    )}

                    <div className="w-100 pl-2">
                      <InputController
                        label={`Icono (${extensions_allowed_image.join(
                          ', '
                        )}) Max: ${sizeMaxFile} MB`}
                        inputType="file"
                        formatAccept="image/*"
                        alidateFormatMsg={`Solo se admite ${extensions_allowed_image.join(
                          ', '
                        )}`}
                        name="file"
                        control={control}
                        validateFormat="imageavatar"
                        changeAction={(e) => handleFile(e)}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-sm-6 mb-3">
                    <InputController
                      id="field-order"
                      label="Orden"
                      name="order"
                      control={control}
                      inputType="number"
                      placeholderTxt="Orden del Grupo"
                      min={1}
                      required
                    />
                  </div>
                  <div className="mb-3 col-12">
                    <InputController
                      label="Descripción *"
                      name="description"
                      inputType="textarea"
                      rows={3}
                      placeholderTxt="Breve descripción del Grupo"
                      control={control}
                      required
                    />
                  </div>
                  <div className="row">
                    <h3>Permisos</h3>
                    <div className="col-12 col-sm-4 mb-3">
                      <CheckboxCustom
                        label="Permitir creación de Reportes en la App"
                        name="allow_create_in_app"
                        control={control}
                        value={true}
                        labelInput="allow_create_in_app"
                        classNameWrap="pt-4"
                        onChange={(e) => {
                          setAllowCreation((prev) => ({
                            ...prev,
                            allow_create_in_app: e.target.checked,
                          }))
                        }}
                      />
                    </div>
                    <div className="col-12 col-sm-4 mb-3">
                      <CheckboxCustom
                        label="Permitir creación de Reportes en la Web"
                        name="allow_create_in_web"
                        control={control}
                        value={true}
                        labelInput="allow_create_in_web"
                        classNameWrap="pt-4"
                        onChange={(e) => {
                          setAllowCreation((prev) => ({
                            ...prev,
                            allow_create_in_web: e.target.checked,
                          }))
                        }}
                      />
                    </div>
                    <div className="col-12 col-sm-4 mb-3">
                      <CheckboxCustom
                        label="Mostrar en sistema"
                        name="show_system"
                        control={control}
                        value={true}
                        labelInput="show_system"
                        classNameWrap="pt-4"
                        onChange={(e) => {
                          setAllowCreation((prev) => ({
                            ...prev,
                            show_system: e.target.checked,
                          }))
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </form>
      </FormProvider>
    </>
  )
}
export default ReportCategoriesNew
